// ---------------------------------------------------------------------------
// Wave 1 Track E — legacy global pricing config + pending-change queue
// ---------------------------------------------------------------------------
// Moved verbatim out of aiUsageApi.ts.
//
// These mirror the Zod-defined request/response shapes in
// @platform/shared-types agent-admin.schema.ts. Some endpoints may not yet be
// live while Wave 1 backend tracks (A/B/C/D) are wiring up — any 404 will
// surface as a typed Error the caller can render in an empty state instead
// of hard-crashing the UI.

import { adminGet, adminSend } from './transport';

// ---------- Pricing (global) ------------------------------------------------

export interface AgentApiRegistryEntry {
  label: string;
  burnRate: number;
  pricing: Record<string, number>;
  minCreditsPerCall: number;
  enabled: boolean;
  lastVerifiedAt?: string;
  lastSyncedAt?: string;
  lastSyncSource?: 'auto' | 'manual' | 'drift_check';
  freeTierCallsPerDay?: number;
}

export interface AgentOperationDefinition {
  apisUsed: string[];
  costModel?: string;
  expectedBreakdown?: Record<string, number>;
  reserveCap?: number;
  feature?: string;
}

export interface AgentPricingChangeHistoryEntry {
  at: string;
  by: string;
  diff: { field: string; before: unknown; after: unknown };
  note?: string;
}

export interface AgentPricingConfig {
  creditUnitPriceUsd: number;
  minPurchaseCredits: number;
  apis: {
    anthropic?: AgentApiRegistryEntry;
    google_vertex?: AgentApiRegistryEntry;
    google_cse?: AgentApiRegistryEntry;
    [key: string]: AgentApiRegistryEntry | undefined;
  };
  operations: Record<string, AgentOperationDefinition>;
  lastUpdatedAt?: string;
  lastUpdatedBy?: string;
  changeHistory?: AgentPricingChangeHistoryEntry[];
}

export interface AgentPricingPutBody {
  creditUnitPriceUsd?: number;
  minPurchaseCredits?: number;
  apis?: Partial<AgentPricingConfig['apis']>;
  operations?: Record<string, AgentOperationDefinition>;
  note?: string;
}

export async function getPricing(): Promise<AgentPricingConfig> {
  const body = await adminGet<{ config: AgentPricingConfig } | AgentPricingConfig>('/agents/admin/pricing');
  return (body as any).config ?? (body as AgentPricingConfig);
}

export async function updatePricing(body: AgentPricingPutBody): Promise<AgentPricingConfig> {
  const parsed = await adminSend<{ config: AgentPricingConfig } | AgentPricingConfig>(
    '/agents/admin/pricing',
    'PUT',
    body,
  );
  return (parsed as any).config ?? (parsed as AgentPricingConfig);
}

// ---------- Pending pricing changes queue -----------------------------------

export interface AgentPricingPendingChange {
  pk: string;
  sk: string;
  detectedAt: string;
  source: 'google_billing_api' | 'anthropic_drift_check' | string;
  api: 'anthropic' | 'google_vertex' | 'google_cse' | string;
  field: string;
  currentValue: number;
  detectedValue: number;
  deltaPercent: number;
  status: 'PENDING_REVIEW' | 'APPLIED' | 'DISMISSED';
  reviewedAt?: string;
  reviewedBy?: string;
  actionNote?: string;
  metadata?: {
    expectedUsd?: number;
    actualUsd?: number;
    drift_sample_date?: string;
  };
}

export async function listPendingChanges(): Promise<AgentPricingPendingChange[]> {
  const parsed = await adminGet<{ items: AgentPricingPendingChange[] } | AgentPricingPendingChange[]>(
    '/agents/admin/pricing/pending',
  );
  return Array.isArray(parsed) ? parsed : (parsed.items ?? []);
}

export async function applyPendingChange(
  sk: string,
  body: { actionNote?: string; adjustBurnRate?: number } = {},
): Promise<void> {
  await adminSend(`/agents/admin/pricing/pending/${encodeURIComponent(sk)}/apply`, 'POST', body);
}

export async function dismissPendingChange(
  sk: string,
  body: { actionNote?: string } = {},
): Promise<void> {
  await adminSend(`/agents/admin/pricing/pending/${encodeURIComponent(sk)}/dismiss`, 'POST', body);
}

// TODO(track-d): endpoint /agents/admin/pricing/resync/{api} is owned by
// Wave 1 Track D (cron pricing sync). Until that ships, this method will
// 404 and the UI should surface a friendly "Force resync not available yet"
// error.
export async function forceResync(api: 'anthropic' | 'google_vertex' | 'google_cse'): Promise<void> {
  await adminSend(`/agents/admin/pricing/resync/${encodeURIComponent(api)}`, 'POST');
}
